import React, { useState, useEffect } from'react';
import Api from'../../services/api';
import { initiateRazorpayPayment, processCashPayment } from'../../services/razorpayService';

const PaymentModal = ({ booking, fare, user, onPaymentComplete, onClose }) => {
 const [paymentMethod, setPaymentMethod] = useState('cash');
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);
 const [razorpayAvailable, setRazorpayAvailable] = useState(true); // Default to true (optimistic)

 // Check Razorpay availability on component mount (non-blocking)
 useEffect(() => {
 const checkRazorpayStatus = async () => {
 try {
 const token = localStorage.getItem('uttaapp_token');
 if (!token) {
 // If no token, keep Razorpay available (will be checked during payment)
 return;
 }

 const response = await fetch(Api.Payment.razorpayStatus, {
 method:'GET',
 headers: {'Authorization':`Bearer ${token}`}
 });

 if (response.ok) {
 const data = await response.json();
 // Only disable if explicitly told it's unavailable
 // If status check fails, keep it available (optimistic approach)
 if (data.razorpayAvailable === false) {
 setRazorpayAvailable(false);
 console.warn('⚠️ Razorpay is not available:', data.message);
 } else {
 setRazorpayAvailable(true);
 console.log('✅ Razorpay is available');
 }
 } else {
 // If status check fails, keep Razorpay available
 // User can try and get proper error message during payment
 console.log('⚠️ Could not check Razorpay status, keeping it available');
 }
 } catch (error) {
 console.error('Error checking Razorpay status:', error);
 // Don't disable Razorpay on error - let user try
 // Error will be shown during actual payment attempt
 }
 };

 checkRazorpayStatus();
 }, []);

 const handlePayment = async () => {
 if (!booking || !fare) {
 setError('Booking information is missing');
 return;
 }

 setLoading(true);
 setError(null);

 try {
 const bookingId = booking._id || booking.id;
 const currentUser = user || {
 name: localStorage.getItem('userName') ||'User',
 email: localStorage.getItem('userEmail') ||'user@example.com',
 phone: localStorage.getItem('userPhone') ||'9999999999'};

 if (paymentMethod ==='cash') {
 // Cash payment - use processCashPayment helper
 try {
 await processCashPayment(bookingId);
 
 console.log('✅ Cash payment completed');
 if (onPaymentComplete) {
 onPaymentComplete({
 paymentMethod:'cash',
 status:'completed'});
 }
 setLoading(false);
 } catch (error) {
 console.error('Error processing cash payment:', error);
 setError(error.message ||'Failed to process cash payment');
 setLoading(false);
 }
 } else if (paymentMethod ==='razorpay') {
 // Razorpay payment flow - using updated initiateRazorpayPayment
 try {
 await initiateRazorpayPayment(
 fare,
 bookingId,
 currentUser,
 async (paymentResponse) => {
 // Payment successful - updatePaymentStatus is already called inside initiateRazorpayPayment
 console.log('✅ Razorpay payment success:', paymentResponse);
 
 if (onPaymentComplete) {
 onPaymentComplete({
 paymentMethod:'razorpay',
 status:'completed',
 transactionId: paymentResponse.razorpay_payment_id
 });
 }
 setLoading(false);
 },
 (error) => {
 // Payment failed or cancelled
 console.error('Payment error:', error);
 const errorMessage = error.message ||'Payment was cancelled or failed';
 
 // If Razorpay is not configured, suggest cash payment
 if (errorMessage.includes('Payment gateway') || 
 errorMessage.includes('Razorpay') || 
 errorMessage.includes('authentication') || 
 errorMessage.includes('configure valid API keys') ||
 errorMessage.includes('Razorpay authentication failed')) {
 setError('Razorpay payment gateway is not configured. Please use Cash payment to complete your booking, or contact admin to configure Razorpay API keys.');
 // Auto-switch to cash payment option
 setPaymentMethod('cash');
 setRazorpayAvailable(false); // Mark as unavailable
 } else {
 setError(errorMessage);
 }
 setLoading(false);
 }
 );
 } catch (error) {
 console.error('Error initiating Razorpay payment:', error);
 const errorMessage = error.message ||'Failed to initiate payment';
 
 // If Razorpay is not configured, suggest cash payment
 if (errorMessage.includes('Payment gateway') || 
 errorMessage.includes('Razorpay') || 
 errorMessage.includes('authentication') || 
 errorMessage.includes('configure valid API keys') ||
 errorMessage.includes('Razorpay authentication failed')) {
 setError('Razorpay payment gateway is not configured. Please use Cash payment to complete your booking, or contact admin to configure Razorpay API keys.');
 // Auto-switch to cash payment option
 setPaymentMethod('cash');
 setRazorpayAvailable(false); // Mark as unavailable
 } else {
 setError(errorMessage);
 }
 setLoading(false);
 }
 }
 } catch (error) {
 console.error('Payment error:', error);
 setError(error.message ||'Something went wrong. Please try again.');
 setLoading(false);
 }
 };

 return (
 <div className="fixed inset-0 z-[10002] flex items-end justify-center bg-black bg-opacity-50 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-[calc(0.75rem+env(safe-area-inset-top))] sm:px-4 md:items-center md:p-4"style={{ display:'flex'}}>
 <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md animate-slideUp flex-col overflow-hidden rounded-2xl bg-white shadow-2xl md:max-h-[calc(100vh-2rem)]">
 {/* Header */}
 <div className="shrink-0 bg-gradient-to-r from-green-500 via-emerald-600 to-green-500 p-4 sm:p-5 md:p-6">
 <div className="mb-3 flex min-w-0 items-center justify-between gap-3 sm:mb-4">
 <h2 className="min-w-0 break-words text-xl font-bold leading-tight text-white sm:text-2xl">Complete Payment</h2>
 <button
 onClick={onClose}
 className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10 hover:text-gray-200 disabled:opacity-60"disabled={loading}
 aria-label="Close payment modal"
 >
 <svg className="h-6 w-6"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>
 <div className="rounded-lg bg-white/20 p-3 backdrop-blur-sm sm:p-4">
 <p className="mb-1 text-sm text-white">Total Amount</p>
 <p className="break-words text-2xl font-black leading-tight text-white sm:text-3xl">₹{fare}</p>
 </div>
 </div>

 {/* Content */}
 <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5 md:p-6">
 {/* Warning Banner - Show only if Razorpay is unavailable */}
 {!razorpayAvailable && (
 <div className="mb-4 rounded-lg border-l-4 border-yellow-500 bg-yellow-50 p-4 sm:mb-6">
 <div className="flex items-start">
 <div className="flex-shrink-0 text-yellow-600">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
 </svg>
 </div>
 <div className="ml-3 min-w-0 flex-1">
 <p className="break-words text-sm font-semibold leading-5 text-yellow-800">
 <span className="block mb-1">Online payment is temporarily unavailable. Please select Cash payment below to complete your booking.</span>
 <span className="text-xs font-normal text-yellow-700 mt-1 block">
 💡 Tip: Cash payment is available and will complete your booking instantly.
 </span>
 </p>
 </div>
 </div>
 </div>
 )}

 {/* Error Message - Show for other errors */}
 {error && !error.includes('Online payment') && (
 <div className="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 p-4 sm:mb-6">
 <div className="flex items-start">
 <div className="flex-shrink-0 text-red-600">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </div>
 <div className="ml-3 min-w-0 flex-1">
 <p className="break-words text-sm font-semibold leading-5 text-red-700">
 {error}
 </p>
 </div>
 </div>
 </div>
 )}

 {/* Payment Methods */}
 <div className="mb-4 space-y-3 sm:mb-6 sm:space-y-4">
 <h3 className="mb-3 text-base font-bold text-gray-900 sm:mb-4 sm:text-lg">Choose Payment Method</h3>
 
 {/* Cash Option */}
 <button
 onClick={() => setPaymentMethod('cash')}
 disabled={loading}
 className={`w-full rounded-xl border-2 p-4 transition-all duration-200 sm:p-5 ${
 paymentMethod ==='cash'?'border-green-500 bg-green-50 shadow-lg md:scale-105 md:transform':'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'} ${loading ?'opacity-50 cursor-not-allowed':'cursor-pointer'}`}
 >
 <div className="flex min-w-0 items-center gap-3 sm:gap-4">
 <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${
 paymentMethod ==='cash'?'bg-green-500':'bg-gray-200'}`}>
 <svg className={`h-5 w-5 sm:h-6 sm:w-6 ${paymentMethod ==='cash'?'text-white':'text-gray-600'}`} fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
 </svg>
 </div>
 <div className="min-w-0 flex-1 text-left">
 <h4 className="break-words text-base font-bold leading-6 text-gray-900 sm:text-lg">Cash Payment</h4>
 <p className="break-words text-sm leading-5 text-gray-600">Pay directly to the driver</p>
 </div>
 {paymentMethod ==='cash'&& (
 <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-500">
 <svg className="h-4 w-4 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M5 13l4 4L19 7"/>
 </svg>
 </div>
 )}
 </div>
 </button>

 {/* Razorpay Option */}
 <button
 onClick={() => {
 if (!razorpayAvailable || (error && error.includes('Online payment'))) {
 // Don't allow switching to Razorpay if it's unavailable
 return;
 }
 setPaymentMethod('razorpay');
 setError(null); // Clear error when switching
 }}
 disabled={loading || !razorpayAvailable || (error && error.includes('Online payment'))}
 className={`w-full rounded-xl border-2 p-4 transition-all duration-200 sm:p-5 ${
 paymentMethod ==='razorpay'?'border-blue-500 bg-blue-50 shadow-lg md:scale-105 md:transform': !razorpayAvailable || (error && error.includes('Online payment'))
 ?'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed':'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'} ${loading ?'opacity-50 cursor-not-allowed':''}`}
 >
 <div className="flex min-w-0 items-center gap-3 sm:gap-4">
 <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${
 paymentMethod ==='razorpay'?'bg-blue-500': !razorpayAvailable || (error && error.includes('Online payment')) ?'bg-gray-300':'bg-gray-200'}`}>
 <svg className={`h-5 w-5 sm:h-6 sm:w-6 ${paymentMethod ==='razorpay'?'text-white': !razorpayAvailable || (error && error.includes('Online payment')) ?'text-gray-500':'text-gray-600'}`} fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
 </svg>
 </div>
 <div className="min-w-0 flex-1 text-left">
 <h4 className="break-words text-base font-bold leading-6 text-gray-900 sm:text-lg">Pay with Razorpay</h4>
 <p className={`break-words text-sm leading-5 ${!razorpayAvailable || (error && error.includes('Online payment')) ?'text-red-600 font-semibold':'text-gray-600'}`}>
 {!razorpayAvailable || (error && error.includes('Online payment')) ?'Currently unavailable':'Secure online payment'}
 </p>
 </div>
 {paymentMethod ==='razorpay'&& razorpayAvailable && !error && (
 <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500">
 <svg className="h-4 w-4 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M5 13l4 4L19 7"/>
 </svg>
 </div>
 )}
 {(!razorpayAvailable || (error && error.includes('Online payment'))) && (
 <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500">
 <svg className="h-4 w-4 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </div>
 )}
 </div>
 </button>
 </div>

 {/* Action Buttons */}
 <div className="sticky bottom-0 -mx-4 -mb-4 flex flex-col gap-3 border-t border-gray-100 bg-white/95 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur sm:-mx-5 sm:-mb-5 sm:flex-row sm:gap-4 sm:p-5 md:-mx-6 md:-mb-6 md:p-6">
 <button
 onClick={onClose}
 className="min-h-[52px] flex-1 rounded-xl bg-gray-100 px-4 py-3.5 text-base font-bold leading-5 text-gray-800 transition-all duration-200 hover:bg-gray-200 disabled:opacity-60 sm:px-6 sm:py-4 sm:text-lg"disabled={loading}
 >
 Cancel
 </button>
 <button
 onClick={handlePayment}
 disabled={loading}
 className={`min-h-[52px] flex-1 rounded-xl px-4 py-3.5 text-base font-bold leading-5 text-white transition-all sm:px-6 sm:py-4 sm:text-lg ${
 paymentMethod ==='cash'?'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700':'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'} ${loading ?'opacity-50 cursor-not-allowed':'shadow-lg hover:shadow-xl md:hover:scale-105 md:transform'}`}
 >
 {loading ? (
 <div className="flex items-center justify-center gap-2">
 <svg className="h-5 w-5 shrink-0 animate-spin"fill="none"viewBox="0 0 24 24">
 <circle className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"></circle>
 <path className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 <span>Processing...</span>
 </div>
 ) : (
 paymentMethod ==='cash'?'Confirm Cash Payment':'Pay with Razorpay')}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default PaymentModal;

